import { eq } from "drizzle-orm";
import cuid from "cuid";

import type {
  CreateWorkspaceScheduledBroadcastResponse,
  NotificationFeedPreference,
  WorkspaceInvitation,
  WorkspaceRole,
  WsFeedPreference,
  WsWorkflowItem,
} from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("feed");

import { client } from "@/config";
import { db } from "@/db/db";
import { feed, item } from "@/db/schema";
import { bulkUpsertBroadcastAction } from "@/models/repositories/BroadcastAction";
import { bulkUpsertDirectWsInvitation } from "@/models/repositories/DirectWsInvitation";
import { bulkUpsertFeedPreference } from "@/models/repositories/FeedPreference";
import { bulkUpsertItemAndCalculateMembership } from "@/models/repositories/Item";
import { bulkUpsertLink } from "@/models/repositories/Link";
import { bulkUpsertScheduleTrigger } from "@/models/repositories/ScheduleTrigger";
import { bulkUpsertWsTranscriptions } from "@/models/repositories/Transcription";
import { bulkUpsertWorkflowItem, deleteWsDraft } from "@/models/repositories/WorkflowItem";
import { bulkUpsertWorkspaceMembership } from "@/models/repositories/WorkspaceMembership";
import { bulkUpsertBroadcastRecipients } from "../repositories";

export async function deleteItem(itemId: string) {
  const feedItem = await db.query.item.findFirst({ where: eq(item.id, itemId) }).execute();
  const feedRecord = await db.query.feed.findFirst({ where: eq(feed.id, feedItem?.feedId) }).execute();
  if (!feedRecord || !feedRecord.workspaceId || !itemId) return;

  const res = await client.deleteWorkspaceFeedItem(feedRecord.workspaceId, feedRecord.id, itemId);

  if (res?.item) {
    await bulkUpsertItemAndCalculateMembership([res.item]);
  }
}

export async function sendWorkspaceInvites(
  workspaceId: string,
  emails: string[] = [],
  phoneNumbers: string[] = [],
  sendEmail = false,
  invites: WorkspaceInvitation[] = [],
) {
  const res = await client.createWorkspaceInvitations(workspaceId, emails, phoneNumbers, sendEmail, invites);
  const invitations = res?.invitations || [];
  const workspaceMemberships = res?.workspaceMemberships || [];
  if (invitations.length) await bulkUpsertDirectWsInvitation(invitations);
  if (workspaceMemberships.length) await bulkUpsertWorkspaceMembership(workspaceMemberships);
  return {
    invitations,
    workspaceMemberships,
  };
}

export async function updateMemberRole(workspaceId: string, workspaceMembershipIds: string[], role: WorkspaceRole) {
  const resp = await client.updateWorkspaceMember(workspaceId, workspaceMembershipIds, role);
  if (resp?.workspaceMemberships?.length) await bulkUpsertWorkspaceMembership(resp.workspaceMemberships);
}

export async function removeMember(workspaceId: string, workspaceMembershipIds: string[]) {
  const resp = await client.removeWorkspaceMember(workspaceId, workspaceMembershipIds);
  if (resp?.workspaceMemberships?.length) await bulkUpsertWorkspaceMembership(resp.workspaceMemberships);
}

export async function subscribeToFeed({
  workspaceId,
  feedId,
  workspaceMembershipId,
  preference,
}: {
  workspaceId: string;
  feedId: string;
  workspaceMembershipId: string;
  preference: NotificationFeedPreference;
}): Promise<WsFeedPreference> {
  if (!preference) {
    throw new Error("Preference is required for subscribing to a feed");
  }
  const response = await client.subscribeToFeed({
    workspaceId,
    feedId,
    workspaceMembershipId,
    preference,
  });
  if (response?.feedPreference) {
    await bulkUpsertFeedPreference([response.feedPreference]);
  }
  return response?.feedPreference ?? null;
}

export async function unsubscribeFromFeed({
  workspaceId,
  feedId,
  workspaceMembershipId,
}: {
  workspaceId: string;
  feedId: string;
  workspaceMembershipId: string;
}): Promise<WsFeedPreference> {
  const { feedPreference } = await client.unsubscribeFromFeed({
    workspaceId,
    feedId,
    workspaceMembershipId,
  });
  await bulkUpsertFeedPreference([feedPreference]);
  return feedPreference;
}

export async function subscribeToFeedGroup({
  workspaceId,
  feedGroupId,
  workspaceMembershipId,
  preference,
}: {
  workspaceId: string;
  feedGroupId: string;
  workspaceMembershipId: string;
  preference: NotificationFeedPreference;
}) {
  const { feedPreferences } = await client.subscribeToFeedGroup({
    workspaceId,
    feedGroupId,
    workspaceMembershipId,
    preference,
  });
  await bulkUpsertFeedPreference(feedPreferences);
  return feedPreferences;
}

export async function unsubscribeFromFeedGroup({
  workspaceId,
  feedGroupId,
  workspaceMembershipId,
}: {
  workspaceId: string;
  feedGroupId: string;
  workspaceMembershipId: string;
}) {
  const { feedPreferences } = await client.unsubscribeFromFeedGroup({
    workspaceId,
    feedGroupId,
    workspaceMembershipId,
  });
  await bulkUpsertFeedPreference(feedPreferences);
  return feedPreferences;
}

export async function subscribeToDriverFeeds({
  workspaceId,
  workspaceMembershipId,
  driverWorkspaceMembershipIds,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  driverWorkspaceMembershipIds: string[];
}) {
  const { feedPreferences } = await client.subscribeToDriverFeeds({
    workspaceId,
    workspaceMembershipId,
    driverWorkspaceMembershipIds,
  });
  await bulkUpsertFeedPreference(feedPreferences);
  return feedPreferences;
}

export async function unsubscribeFromDriverFeeds({
  workspaceId,
  workspaceMembershipId,
  driverWorkspaceMembershipIds,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  driverWorkspaceMembershipIds: string[];
}) {
  const { feedPreferences } = await client.unsubscribeFromDriverFeeds({
    workspaceId,
    workspaceMembershipId,
    driverWorkspaceMembershipIds,
  });
  await bulkUpsertFeedPreference(feedPreferences);
  return feedPreferences;
}

export async function createWorkflowItem(workspaceId: string, contentId?: string, displayName?: string, text?: string) {
  const payload = contentId ? { contentId, workspaceId, displayName } : { displayName, inputText: text, workspaceId };

  const workflowItem: WsWorkflowItem = await client.createWorkspaceWorkflowItem(payload);

  if (!workflowItem) throw new Error("WorkflowItem not created");
  await bulkUpsertWorkflowItem([workflowItem]);
  return workflowItem;
}

export async function deleteWorkflowItem(workspaceId: string, workflowItemId: string) {
  await client.deleteWorkspaceWorkflowItem(workspaceId, workflowItemId);
  await deleteWsDraft(workflowItemId);
}

export async function createScheduledWorkflowItem({
  workspaceId,
  workflowItemId,
  feedIds,
  scheduledDate,
  scheduledCron,
  timezone,
}: {
  workspaceId: string;
  workflowItemId: string;
  feedIds: Array<string>;
  scheduledDate?: string;
  scheduledCron?: string;
  timezone?: string;
}): Promise<CreateWorkspaceScheduledBroadcastResponse> {
  let props: Parameters<typeof client.createScheduledBroadcast>[0] = {
    workspaceId: workspaceId,
    feedIds,
    workflowItemId: workflowItemId,
  };

  if (scheduledDate) {
    props = { ...props, date: scheduledDate };
  }

  if (scheduledCron) {
    props = { ...props, cron: scheduledCron, timezone: timezone };
  }

  const scheduledBroadcast = await client.createScheduledBroadcast(props);

  if (!scheduledBroadcast) throw new Error("scheduledBroadcast not created");

  log.debug("created broadcast", scheduledBroadcast);
  if (scheduledBroadcast) {
    if (scheduledBroadcast?.scheduleTrigger) {
      await bulkUpsertScheduleTrigger([scheduledBroadcast?.scheduleTrigger]);
    }

    if (scheduledBroadcast?.broadcastAction) {
      await bulkUpsertBroadcastAction([scheduledBroadcast?.broadcastAction]);
    }

    if (scheduledBroadcast?.broadcastRecipients) {
      await bulkUpsertBroadcastRecipients(scheduledBroadcast?.broadcastRecipients || []);
    }
  }

  return scheduledBroadcast;
}

export async function updateScheduledWorkflowItem({
  workspaceId,
  scheduleId,
  feedIds,
  scheduledDate,
  scheduledCron,
  timezone,
}: {
  workspaceId: string;
  scheduleId: string;
  feedIds: Array<string>;
  scheduledDate?: string;
  scheduledCron?: string;
  timezone?: string;
}) {
  const props: {
    workspaceId: string;
    feedIds: string[];
    scheduleId: string;
    date?: string;
    cron?: string;
    timezone?: string;
  } = { workspaceId, feedIds, scheduleId };
  if (scheduledDate) props.date = scheduledDate;
  if (scheduledCron) {
    props.cron = scheduledCron;
    props.timezone = timezone;
  }

  const scheduledBroadcast = await client.updateScheduledBroadcast(props);
  if (!scheduledBroadcast) throw new Error("scheduledBroadcast not updated");

  if (scheduledBroadcast?.scheduleTrigger) {
    await bulkUpsertScheduleTrigger([scheduledBroadcast?.scheduleTrigger]);
  }

  if (scheduledBroadcast?.broadcastAction) {
    await bulkUpsertBroadcastAction([scheduledBroadcast?.broadcastAction]);
  }

  if (scheduledBroadcast?.broadcastRecipients) {
    await bulkUpsertBroadcastRecipients(scheduledBroadcast.broadcastRecipients);
  }
}

export async function publishToWorkspaceFeed({
  workspaceId,
  feedId,
  itemId,
  contentId,
  groupId,
  text,
  preferredLanguage,
  isSilent,
}: {
  workspaceId: string;
  feedId: string;
  itemId?: string;
  contentId: string;
  groupId?: string;
  text?: string;
  preferredLanguage?: string;
  isSilent?: boolean;
}) {
  const feedItemId = itemId || cuid();
  const res = await client.publishTTS({
    workspaceId,
    feedId,
    itemId: feedItemId,
    contentId,
    groupId,
    text,
    preferredLanguage,
    isSilent,
  });
  if (!res) {
    throw new Error("Failed to publish TTS: received an empty response");
  }
  const { item, links, transcriptions } = res;
  await bulkUpsertItemAndCalculateMembership([item]);
  if (links?.length) await bulkUpsertLink(links);
  if (transcriptions?.length) await bulkUpsertWsTranscriptions(transcriptions);
  return item;
}
